import React, { useState, useMemo } from 'react';
import { Bucket, BucketTheme, Todo } from '../types';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, CheckCircle2, Circle, Clock, Pencil, X, Check, Repeat, CalendarClock, CalendarDays, Calendar, ChevronDown, Plus } from 'lucide-react';
import { useLanguage } from '../i18n';

interface CalendarViewProps {
  buckets: Bucket[];
  onUpdateBucket: (id: string, updates: Partial<Bucket>) => void;
  onDeleteBucket: (id: string) => void;
  onCreateBucket?: () => void;
}

const THEME_COLORS: Record<BucketTheme, { bg: string; text: string; border: string }> = {
  [BucketTheme.Blue]: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
  [BucketTheme.Purple]: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
  [BucketTheme.Green]: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  [BucketTheme.Orange]: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30' },
  [BucketTheme.Pink]: { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30' },
  [BucketTheme.Red]: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
  [BucketTheme.Teal]: { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-500/30' },
  [BucketTheme.Yellow]: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30' },
  [BucketTheme.Indigo]: { bg: 'bg-indigo-500/20', text: 'text-indigo-300', border: 'border-indigo-500/30' },
  [BucketTheme.Cyan]: { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30' },
  [BucketTheme.Lime]: { bg: 'bg-lime-500/20', text: 'text-lime-300', border: 'border-lime-500/30' },
  [BucketTheme.Rose]: { bg: 'bg-rose-500/20', text: 'text-rose-300', border: 'border-rose-500/30' },
};

type ViewMode = 'month' | 'week' | 'day';

interface EditDataState {
    text: string;
    points: number;
    recurrence?: 'daily' | 'weekly' | 'monthly';
    scheduledDate?: string;
}

interface CalendarTodoItemProps {
    item: { todo: Todo, bucket: Bucket };
    viewMode: ViewMode;
    editingTodoId: string | null;
    editData: EditDataState;
    setEditData: React.Dispatch<React.SetStateAction<EditDataState>>;
    onSaveEdit: (id: string) => void;
    onCancelEdit: () => void;
    onDeleteTodo: (id: string) => void;
    onToggleTodo: (id: string) => void;
    onStartEdit: (todo: Todo) => void;
}

interface MonthDay { day: number; padding: boolean; }
interface WeekDay { day: number; padding: boolean; date: Date; key: string; }

interface MonthGridData { type: 'month'; days: MonthDay[]; year: number; month: number; todayKey: string; }
interface WeekGridData { type: 'week'; days: WeekDay[]; todayKey: string; }
interface DayGridData { type: 'day'; date: Date; key: string; todayKey: string; }

type GridData = MonthGridData | WeekGridData | DayGridData | null;

const CalendarTodoItem: React.FC<CalendarTodoItemProps> = ({ 
    item, viewMode, editingTodoId, editData, setEditData, 
    onSaveEdit, onCancelEdit, onDeleteTodo, onToggleTodo, onStartEdit 
}) => {
    const { t } = useLanguage();
    const isEditing = editingTodoId === item.todo.id;
    const colors = THEME_COLORS[item.bucket.theme];

    if (isEditing) {
        return (
           <div className="p-2 rounded-xl bg-slate-900 border border-indigo-500 shadow-xl z-10 relative space-y-2 min-w-[200px]">
              <input 
                  type="text" 
                  value={editData.text}
                  onChange={(e) => setEditData({...editData, text: e.target.value})}
                  className="w-full bg-transparent border-b border-white/20 px-2 py-1 text-sm text-white focus:outline-none focus:border-indigo-400 transition-colors"
                  autoFocus
              />
              <div className="flex items-center gap-2">
                  <div className="relative w-14">
                      <select
                          value={editData.points}
                          onChange={(e) => setEditData({...editData, points: parseInt(e.target.value)})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg pl-2 pr-5 py-1 text-xs text-white appearance-none focus:outline-none"
                      >
                          {[1, 2, 3, 4, 5].map(p => <option key={p} value={p} className="bg-slate-900">{p} pts</option>)}
                      </select>
                      <ChevronDown size={10} className="absolute right-1 top-1.5 text-white/40 pointer-events-none" />
                  </div>
                   <div className="relative w-8">
                      <button
                          type="button"
                          onClick={() => {
                              if (!editData.recurrence) setEditData({...editData, recurrence: 'daily'});
                              else if (editData.recurrence === 'daily') setEditData({...editData, recurrence: 'weekly'});
                              else if (editData.recurrence === 'weekly') setEditData({...editData, recurrence: 'monthly'});
                              else setEditData({...editData, recurrence: undefined});
                          }}
                          className={`w-full h-6 border border-white/10 rounded-lg flex items-center justify-center transition-all ${editData.recurrence ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50' : 'bg-white/5 text-white/30'}`}
                      >
                          {!editData.recurrence && <Repeat size={12} />}
                          {editData.recurrence === 'daily' && <CalendarClock size={12} />}
                          {editData.recurrence === 'weekly' && <CalendarDays size={12} />}
                          {editData.recurrence === 'monthly' && <Calendar size={12} />}
                      </button>
                   </div>
                    <div className="relative w-8">
                            <input 
                              type="date"
                              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
                              value={editData.scheduledDate || ''}
                              onChange={(e) => setEditData({...editData, scheduledDate: e.target.value})}
                            />
                            <div className={`w-full h-6 border border-white/10 rounded-lg flex items-center justify-center transition-all ${editData.scheduledDate ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50' : 'bg-white/5 text-white/30'}`}>
                                <Calendar size={12} />
                            </div>
                    </div>
              </div>
              <div className="flex items-center gap-1 justify-end mt-1">
                   <button onClick={() => onSaveEdit(item.todo.id)} className="p-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg"><Check size={12} /></button>
                   <button onClick={onCancelEdit} className="p-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg"><X size={12} /></button>
              </div>
           </div>
        );
    }

    if (viewMode === 'month') {
        return (
          <div className={`group/item flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] truncate ${item.todo.completed ? 'opacity-50 grayscale' : ''} ${colors.bg} ${colors.border} ${colors.text} cursor-pointer hover:brightness-110`}>
              <div onClick={(e) => { e.stopPropagation(); onToggleTodo(item.todo.id); }} className="flex-shrink-0 hover:text-white transition-colors">{item.todo.completed ? <CheckCircle2 size={10} /> : <Circle size={10} />}</div>
              <span className="truncate flex-1 font-medium">{item.todo.text}</span>
              <div className="opacity-0 group-hover/item:opacity-100 flex items-center gap-1"><button onClick={(e) => { e.stopPropagation(); onStartEdit(item.todo); }} className="text-white/40 hover:text-white"><Pencil size={8} /></button><button onClick={(e) => { e.stopPropagation(); onDeleteTodo(item.todo.id); }} className="text-white/40 hover:text-red-400"><X size={8} /></button></div>
          </div>
        );
    }
    if (viewMode === 'week') {
         return (
              <div className={`group/item flex flex-row items-center gap-3 p-3 rounded-xl border text-xs ${item.todo.completed ? 'opacity-50 grayscale' : ''} ${colors.bg} ${colors.border} ${colors.text} cursor-pointer hover:shadow-lg transition-all`}>
                  <div onClick={(e) => { e.stopPropagation(); onToggleTodo(item.todo.id); }} className="hover:text-white transition-colors shrink-0">{item.todo.completed ? <CheckCircle2 size={16} /> : <Circle size={16} />}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-black text-[8px] uppercase tracking-tighter shrink-0 opacity-60">{item.bucket.name}</span>
                        <div className="flex items-center gap-1 opacity-60 text-[9px]"><Clock size={9} />{item.todo.points} {t.pts}</div>
                    </div>
                    <span className="font-medium truncate block leading-tight">{item.todo.text}</span>
                  </div>
                  <div className="opacity-0 group-hover/item:opacity-100 flex items-center gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); onStartEdit(item.todo); }} className="text-white/40 hover:text-white p-1"><Pencil size={12} /></button>
                      <button onClick={(e) => { e.stopPropagation(); onDeleteTodo(item.todo.id); }} className="text-white/40 hover:text-red-400 p-1"><X size={12} /></button>
                  </div>
              </div>
         );
    }
    return (
          <div className={`group/item flex items-center gap-4 p-4 rounded-xl border transition-all ${item.todo.completed ? 'opacity-60 bg-black/20' : 'bg-slate-900/50'} ${colors.border}`}>
              <div onClick={() => onToggleTodo(item.todo.id)} className={`p-2 rounded-full cursor-pointer hover:scale-110 transition-transform ${colors.bg} ${colors.text}`}>{item.todo.completed ? <CheckCircle2 size={24} /> : <Circle size={24} />}</div>
              <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 ${colors.text}`}>{item.bucket.name}</span><span className="text-xs text-slate-500 bg-white/5 px-2 py-0.5 rounded-full border border-white/5">{item.todo.points} {t.pts}</span></div>
                  <h4 className={`text-lg font-medium break-words ${item.todo.completed ? 'line-through text-slate-500' : 'text-white'}`}>{item.todo.text}</h4>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover/item:opacity-100 transition-opacity"><button onClick={() => onStartEdit(item.todo)} className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"><Pencil size={18} /></button><button onClick={() => onDeleteTodo(item.todo.id)} className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-red-400 hover:bg-white/10"><X size={18} /></button></div>
          </div>
    );
};

export const CalendarView: React.FC<CalendarViewProps> = ({ buckets, onUpdateBucket, onCreateBucket }) => {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const [editData, setEditData] = useState<EditDataState>({ text: '', points: 1 });

  const next = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const prev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const resetToToday = () => setCurrentDate(new Date());

  const handleStartEdit = (todo: Todo) => {
    setEditingTodoId(todo.id);
    setEditData({ text: todo.text, points: todo.points, recurrence: todo.recurrence, scheduledDate: todo.scheduledDate ? new Date(todo.scheduledDate).toISOString().split('T')[0] : '' });
  };

  const handleCancelEdit = () => setEditingTodoId(null);

  const getBucketForTodo = (todoId: string) => buckets.find(b => b.todos.some(t => t.id === todoId));

  const handleSaveEdit = (todoId: string) => {
    const bucket = getBucketForTodo(todoId);
    if (!bucket || !editData.text.trim()) return;
    let scheduledTimestamp: number | undefined = undefined;
    if (editData.scheduledDate) {
        const date = new Date(editData.scheduledDate);
        if (!isNaN(date.getTime())) scheduledTimestamp = date.getTime();
    }
    const updatedTodos = bucket.todos.map(t => t.id === todoId ? { ...t, text: editData.text, points: editData.points, recurrence: editData.recurrence, scheduledDate: scheduledTimestamp } : t);
    onUpdateBucket(bucket.id, { todos: updatedTodos });
    setEditingTodoId(null);
  };

  const handleDeleteTodo = (todoId: string) => {
      if(!confirm("Delete this task?")) return;
      const bucket = getBucketForTodo(todoId);
      if(!bucket) return;
      onUpdateBucket(bucket.id, { todos: bucket.todos.filter(t => t.id !== todoId) });
  };

  const handleToggleTodo = (todoId: string) => {
    const bucket = getBucketForTodo(todoId);
    if(!bucket) return;
    const todo = bucket.todos.find(t => t.id === todoId);
    if(!todo) return;
    const isCompleting = !todo.completed;
    const updatedTodos = bucket.todos.map(t => t.id === todoId ? { ...t, completed: isCompleting, completedAt: isCompleting ? Date.now() : undefined } : t);
    onUpdateBucket(bucket.id, { todos: updatedTodos });
  };

  const todosByDate = useMemo(() => {
    const map = new Map<string, { todo: Todo, bucket: Bucket }[]>();
    buckets.forEach(bucket => {
      bucket.todos.forEach(todo => {
        if (todo.scheduledDate) {
          const date = new Date(todo.scheduledDate);
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          if (!map.has(key)) map.set(key, []);
          map.get(key)!.push({ todo, bucket });
        }
      });
    });
    return map;
  }, [buckets]);

  const generateGrid = (): GridData => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const todayKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    if (viewMode === 'month') {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const days: MonthDay[] = [];
        for (let i = 0; i < firstDay; i++) days.push({ day: 0, padding: true });
        for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, padding: false });
        while (days.length % 7 !== 0) days.push({ day: 0, padding: true });
        return { type: 'month', days, year, month, todayKey };
    } 
    if (viewMode === 'week') {
        const curr = new Date(currentDate);
        const day = curr.getDay();
        const first = curr.getDate() - day; 
        const days: WeekDay[] = [];
        for (let i = 0; i < 7; i++) {
            const next = new Date(curr);
            next.setDate(first + i);
            days.push({ day: next.getDate(), padding: false, date: next, key: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}` });
        }
        return { type: 'week', days, todayKey };
    }
    if (viewMode === 'day') return { type: 'day', date: currentDate, key: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`, todayKey };
    return null;
  };

  const gridData = generateGrid();

  const getHeaderText = () => {
    const year = currentDate.getFullYear();
    const monthName = t.monthNames[currentDate.getMonth()];
    if (viewMode === 'month') return `${monthName} ${year}`;
    if (viewMode === 'day') return `${currentDate.getDate()}. ${monthName} ${year}`;
    if (viewMode === 'week') {
        const start = new Date(currentDate); start.setDate(start.getDate() - start.getDay());
        const end = new Date(start); end.setDate(end.getDate() + 6);
        if (start.getMonth() === end.getMonth()) return `${t.monthNames[start.getMonth()]} ${year}`;
        return `${t.monthNames[start.getMonth()].slice(0,3)} - ${t.monthNames[end.getMonth()].slice(0,3)} ${year}`;
    }
    return "";
  };

  if (buckets.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center animate-in fade-in duration-700">
            <div className="w-24 h-24 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 relative">
                 <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full"></div>
                 <CalendarIcon size={48} className="text-indigo-400 relative z-10" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">{t.calendarView}</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8">{t.introSubtitle}</p>
            {onCreateBucket && (
                <button onClick={onCreateBucket} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-xl shadow-indigo-600/20 active:scale-95"><Plus size={20} />{t.createFirstBucket}</button>
            )}
        </div>
      );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 shrink-0">
        <div className="flex items-center gap-4"><h2 className="text-2xl font-bold text-white flex items-center gap-3"><CalendarIcon className="text-indigo-400" />{getHeaderText()}</h2></div>
        <div className="flex items-center gap-4">
            <div className="bg-slate-900/50 p-1 rounded-xl border border-white/10 flex">
                {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
                    <button key={mode} onClick={() => setViewMode(mode)} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${viewMode === mode ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>{mode === 'month' ? t.viewMonth : mode === 'week' ? t.viewWeek : t.viewDay}</button>
                ))}
            </div>
            <div className="flex items-center gap-2 bg-slate-900/50 p-1 rounded-xl border border-white/10"><button onClick={prev} className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"><ChevronLeft size={20} /></button><button onClick={resetToToday} className="px-3 py-1.5 text-sm font-medium hover:bg-white/10 rounded-lg text-slate-300 hover:text-white transition-colors">{t.today}</button><button onClick={next} className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors"><ChevronRight size={20} /></button></div>
        </div>
      </div>
      <div className="bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden shadow-2xl flex-1 flex flex-col min-h-[500px]">
        {gridData?.type === 'month' && (
            <>
                <div className="grid grid-cols-7 border-b border-white/5 bg-white/5">{t.dayNamesShort.map((day, i) => (<div key={i} className="py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">{day}</div>))}</div>
                <div className="grid grid-cols-7 min-h-[500px] auto-rows-fr divide-x divide-white/5 divide-y h-full">
                {(gridData as MonthGridData).days.map((cell, idx) => {
                    if (cell.padding) return <div key={idx} className="bg-slate-950/30"></div>;
                    const mData = gridData as MonthGridData;
                    const dateKey = `${mData.year}-${String(mData.month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`;
                    const isToday = dateKey === mData.todayKey;
                    const tasks = todosByDate.get(dateKey) || [];
                    const maxTasksToShow = 4;
                    const hasMore = tasks.length > maxTasksToShow;
                    return (
                        <div key={idx} className={`p-2 relative group hover:bg-white/[0.02] transition-colors ${isToday ? 'bg-indigo-500/5' : ''}`}>
                            <div className="flex justify-between items-start mb-2"><span className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium ${isToday ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30' : 'text-slate-400'}`}>{cell.day}</span>{tasks.length > 0 && (<span className="text-[10px] text-slate-500 font-medium">{tasks.length}</span>)}</div>
                            <div className="space-y-1.5 overflow-hidden">{tasks.slice(0, maxTasksToShow).map((item) => (<CalendarTodoItem key={item.todo.id} item={item} viewMode="month" editingTodoId={editingTodoId} editData={editData} setEditData={setEditData} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} onDeleteTodo={handleDeleteTodo} onToggleTodo={handleToggleTodo} onStartEdit={handleStartEdit} />))}{hasMore && (<div className="text-[10px] text-slate-500 pl-2 pt-0.5">{t.moreTasks(tasks.length - maxTasksToShow)}</div>)}</div>
                        </div>
                    );
                })}
                </div>
            </>
        )}
        {gridData?.type === 'week' && (
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                <div className="space-y-2">
                    {(gridData as WeekGridData).days.map((d, i) => {
                        const isToday = d.key === (gridData as WeekGridData).todayKey;
                        const tasks = todosByDate.get(d.key!) || [];
                        return (
                            <div key={i} className={`flex items-stretch gap-4 p-4 rounded-2xl border transition-all min-h-[80px] ${isToday ? 'bg-indigo-500/5 border-indigo-500/20 shadow-lg shadow-indigo-500/5' : 'bg-slate-900/40 border-white/5 hover:border-white/10'}`}>
                                {/* Day Indicator */}
                                <div className="flex flex-col items-center gap-1 w-14 shrink-0 justify-center">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">{t.dayNamesShort[i]}</span>
                                    <span className={`w-10 h-10 flex items-center justify-center rounded-xl text-lg font-black ${isToday ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30' : 'bg-white/5 text-slate-300'}`}>{d.day}</span>
                                </div>
                                
                                {/* Vertical Task Stack within Day Row */}
                                <div className="flex-1 min-w-0">
                                    {tasks.length === 0 ? (
                                        <div className="h-full flex items-center px-4 border border-dashed border-white/5 rounded-xl text-[10px] text-slate-600 font-medium italic">
                                            No tasks scheduled
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            {tasks.map((item) => (
                                                <div key={item.todo.id} className="w-full">
                                                    <CalendarTodoItem 
                                                        item={item} 
                                                        viewMode="week" 
                                                        editingTodoId={editingTodoId} 
                                                        editData={editData} 
                                                        setEditData={setEditData} 
                                                        onSaveEdit={handleSaveEdit} 
                                                        onCancelEdit={handleCancelEdit} 
                                                        onDeleteTodo={handleDeleteTodo} 
                                                        onToggleTodo={handleToggleTodo} 
                                                        onStartEdit={handleStartEdit} 
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
        {gridData?.type === 'day' && (
             <div className="flex flex-col h-full overflow-hidden">
                <div className="p-4 border-b border-white/5 bg-white/5 flex items-center justify-between"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-indigo-500 rounded-2xl flex flex-col items-center justify-center shadow-lg shadow-indigo-500/20"><span className="text-xs text-indigo-200 font-medium uppercase">{t.dayNamesShort[(gridData as DayGridData).date.getDay()]}</span><span className="text-xl font-bold text-white leading-none">{(gridData as DayGridData).date.getDate()}</span></div><div><h3 className="text-lg font-bold text-white">{t.tasks}</h3><p className="text-slate-400 text-sm">{(todosByDate.get((gridData as DayGridData).key!) || []).length} scheduled</p></div></div></div>
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar"><div className="max-w-3xl mx-auto space-y-3">{(todosByDate.get((gridData as DayGridData).key!) || []).length === 0 ? (<div className="text-center py-20 text-slate-500"><CalendarIcon size={48} className="mx-auto mb-4 opacity-20" /><p>No tasks scheduled for this day.</p></div>) : ((todosByDate.get((gridData as DayGridData).key!) || []).map(item => (<CalendarTodoItem key={item.todo.id} item={item} viewMode="day" editingTodoId={editingTodoId} editData={editData} setEditData={setEditData} onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} onDeleteTodo={handleDeleteTodo} onToggleTodo={handleToggleTodo} onStartEdit={handleStartEdit} />)))}</div></div>
             </div>
        )}
      </div>
    </div>
  );
};
